import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Activity, ChevronRight, Shield, Zap, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Category icons and colors
const categoryConfig = {
  phishing_domain: { icon: '🎣', color: 'text-red-400', label: 'Phishing' },
  malicious_url: { icon: '🚨', color: 'text-red-500', label: 'Malicious URL' },
  malware_distribution: { icon: '☠️', color: 'text-red-600', label: 'Malware' },
  crypto_scam: { icon: '💰', color: 'text-amber-400', label: 'Crypto Scam' },
  delivery_scam: { icon: '📦', color: 'text-orange-400', label: 'Delivery Scam' },
  whatsapp_scam: { icon: '💬', color: 'text-green-400', label: 'WhatsApp Scam' },
  email_scam: { icon: '📧', color: 'text-blue-400', label: 'Email Scam' },
  wallet_threat: { icon: '👛', color: 'text-purple-400', label: 'Wallet Threat' },
  suspicious_company: { icon: '🏢', color: 'text-yellow-400', label: 'Suspicious Company' },
  general_threat: { icon: '⚠️', color: 'text-amber-500', label: 'Threat Alert' },
};

// Risk level colors
const riskColors = {
  critical: 'text-red-500',
  high: 'text-red-400',
  medium: 'text-amber-400',
  low: 'text-yellow-400',
};

// Format time ago
const formatTimeAgo = (dateString) => {
  if (!dateString) return 'Recently';
  
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  } catch {
    return 'Recently';
  }
};

// Transform threat item to ticker format
const transformThreatItem = (item) => {
  const config = categoryConfig[item.category] || categoryConfig.general_threat;
  const riskColor = riskColors[item.risk_level] || riskColors.medium;
  
  return {
    id: item.id,
    icon: config.icon,
    label: config.label,
    message: item.title,
    entity: item.entity,
    time: formatTimeAgo(item.timestamp),
    color: config.color,
    riskColor: riskColor,
    riskLevel: item.risk_level,
    source: item.source,
    category: item.category,
  };
};

const ScamIntelligenceTicker = () => {
  const [alerts, setAlerts] = useState([]);
  const [isLive, setIsLive] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [sources, setSources] = useState({});
  const navigate = useNavigate();

  // Fetch live threats from backend
  const fetchThreats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/live-threats`);
      if (!response.ok) throw new Error('Failed to fetch threats');
      
      const data = await response.json();
      
      if (data.success && data.items && data.items.length > 0) {
        const transformedAlerts = data.items.map(transformThreatItem);
        setAlerts(transformedAlerts);
        setIsLive(true);
        setSources(data.sources || {});
        setLastUpdate(data.last_updated ? new Date(data.last_updated) : new Date());
      } else {
        setIsLive(false);
      }
      
      setIsLoading(false);
    } catch (error) {
      console.warn('Ticker: Error fetching threats', error);
      setIsLoading(false);
      setIsLive(false);
    }
  }, []);

  // Initial fetch and periodic refresh
  useEffect(() => {
    fetchThreats();
    
    // Refresh every 60 seconds (backend caches for 5 mins)
    const refreshInterval = setInterval(fetchThreats, 60000);
    
    return () => clearInterval(refreshInterval);
  }, [fetchThreats]);

  // Rotate through alerts
  useEffect(() => {
    if (alerts.length <= 1) return;
    
    const rotateInterval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % alerts.length);
    }, 4500); // 4.5 seconds per alert
    
    return () => clearInterval(rotateInterval);
  }, [alerts.length]);

  // Handle click - navigate to scam checker with entity prefilled
  const handleAlertClick = (alert) => {
    if (alert.entity) {
      // Navigate to scam check page with entity prefilled
      navigate(`/check?entity=${encodeURIComponent(alert.entity)}`);
    }
  };

  // Handle scroll to checker
  const handleScrollToChecker = (e) => {
    e.preventDefault();
    const checkerSection = document.getElementById('scam-checker-section');
    const checkerInput = document.querySelector('[data-testid="scam-checker-input"]');
    
    if (checkerSection) {
      checkerSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (checkerInput) {
        setTimeout(() => checkerInput.focus(), 600);
      }
    }
  };

  const currentAlert = alerts[currentIndex];

  return (
    <div className="scam-ticker-wrapper bg-[#050c15] border-b border-cyan-500/15 relative overflow-hidden">
      {/* Subtle glow line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" />
      
      <div className="flex items-center h-10">
        {/* Left Label - LIVE indicator */}
        <div className="flex-shrink-0 bg-[#0a1628]/80 border-r border-cyan-500/15 px-3 h-full flex items-center gap-2 z-10">
          <div className="flex items-center gap-1.5">
            <div className="relative">
              <Zap className="w-3.5 h-3.5 text-cyan-400" />
              <span className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${isLive ? 'bg-green-400 animate-pulse' : 'bg-cyan-400'}`} />
            </div>
            <span className="text-cyan-400 font-bold text-[11px] tracking-wider uppercase whitespace-nowrap">
              LIVE
            </span>
          </div>
          <div className="hidden md:flex items-center gap-1 px-1.5 py-0.5 bg-cyan-500/10 rounded border border-cyan-500/20">
            <Shield className="w-2.5 h-2.5 text-cyan-500/70" />
            <span className="text-cyan-500/70 text-[9px] tracking-wide whitespace-nowrap font-medium">
              Threat Intelligence
            </span>
          </div>
        </div>

        {/* Main Ticker Content - Single rotating alert with fade */}
        <div className="flex-1 overflow-hidden relative">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <Activity className="w-4 h-4 text-cyan-400 animate-spin" />
              <span className="text-gray-500 text-xs ml-2">Loading threat feed...</span>
            </div>
          ) : alerts.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <Shield className="w-4 h-4 text-cyan-400" />
              <span className="text-gray-400 text-xs ml-2">Monitoring for threats...</span>
            </div>
          ) : (
            <div 
              className="ticker-content flex items-center h-full px-4 cursor-pointer hover:bg-white/5 transition-colors"
              onClick={() => handleAlertClick(currentAlert)}
              title={`Check: ${currentAlert.entity}`}
            >
              {/* Alert Icon */}
              <span className="text-base mr-2 flex-shrink-0">{currentAlert.icon}</span>
              
              {/* Risk Level Badge */}
              <span className={`${currentAlert.riskColor} text-[9px] font-bold uppercase px-1.5 py-0.5 bg-current/10 rounded mr-2 flex-shrink-0`}>
                {currentAlert.riskLevel}
              </span>
              
              {/* Alert Message */}
              <span className="text-gray-300 text-xs font-medium truncate max-w-md">
                {currentAlert.message}
              </span>
              
              {/* Source & Time */}
              <span className="text-gray-600 text-[10px] ml-3 flex-shrink-0 hidden lg:inline">
                via {currentAlert.source}
              </span>
              <span className="text-gray-600 text-[10px] ml-2 flex-shrink-0">
                • {currentAlert.time}
              </span>
              
              {/* Click indicator */}
              <ExternalLink className="w-3 h-3 text-gray-600 ml-2 flex-shrink-0 opacity-0 group-hover:opacity-100" />
            </div>
          )}
          
          {/* Progress bar for rotation */}
          {alerts.length > 1 && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-800">
              <div 
                className="h-full bg-cyan-500/50 transition-all duration-100"
                style={{ width: `${((currentIndex + 1) / alerts.length) * 100}%` }}
              />
            </div>
          )}
        </div>

        {/* Alert Counter */}
        {alerts.length > 0 && (
          <div className="hidden sm:flex items-center px-2 text-gray-600 text-[10px] border-l border-cyan-500/10">
            <span className="text-cyan-400 font-mono">{currentIndex + 1}</span>
            <span className="mx-0.5">/</span>
            <span className="font-mono">{alerts.length}</span>
          </div>
        )}

        {/* Right CTA */}
        <button 
          onClick={handleScrollToChecker}
          className="flex-shrink-0 bg-[#1e40af]/70 hover:bg-[#1e40af] px-4 h-full flex items-center gap-1.5 transition-all z-10 group border-l border-cyan-500/15"
        >
          <span className="text-blue-100 text-[11px] font-semibold whitespace-nowrap">Check Now</span>
          <ChevronRight className="w-3.5 h-3.5 text-blue-200 group-hover:translate-x-0.5 transition-transform" />
        </button>
      </div>

      {/* Ticker Animation Styles */}
      <style>{`
        .ticker-content {
          animation: tickerFadeIn 0.5s ease-out;
        }
        
        @keyframes tickerFadeIn {
          from {
            opacity: 0;
            transform: translateY(4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default ScamIntelligenceTicker;
